import React, { useState } from 'react';

interface YouTubeInputProps {
  onAdd: (url: string) => void;
}

export function YouTubeInput({ onAdd }: YouTubeInputProps) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const validateAndTransformYouTubeUrl = (inputUrl: string) => {
    // Remove whitespace
    const trimmedUrl = inputUrl.trim();
    
    // Extract video ID using various patterns
    let videoId: string | null = null;
    
    // youtube.com/watch?v=VIDEO_ID
    const watchMatch = trimmedUrl.match(/(?:youtube\.com\/watch\?v=)([^&\s]+)/);
    if (watchMatch) videoId = watchMatch[1];
    
    // youtu.be/VIDEO_ID
    const shortMatch = trimmedUrl.match(/(?:youtu\.be\/)([^?\s]+)/);
    if (shortMatch) videoId = shortMatch[1];
    
    // youtube.com/embed/VIDEO_ID
    const embedMatch = trimmedUrl.match(/(?:youtube\.com\/embed\/)([^?\s]+)/);
    if (embedMatch) videoId = embedMatch[1];
    
    // youtube.com/v/VIDEO_ID
    const vMatch = trimmedUrl.match(/(?:youtube\.com\/v\/)([^?\s]+)/);
    if (vMatch) videoId = vMatch[1];
    
    // youtube.com/shorts/VIDEO_ID
    const shortsMatch = trimmedUrl.match(/(?:youtube\.com\/shorts\/)([^?\s]+)/);
    if (shortsMatch) videoId = shortsMatch[1];

    if (!videoId) {
      throw new Error('Invalid YouTube URL. Please enter a valid YouTube video URL.');
    }

    // Return the embed URL
    return `https://www.youtube.com/embed/${videoId}`;
  };

  const handleAdd = () => {
    try {
      setError(null);
      const embedUrl = validateAndTransformYouTubeUrl(url);
      onAdd(embedUrl);
      setUrl('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid YouTube URL');
    }
  };

  return (
    <div>
      <div className="flex gap-2">
        <input
          type="text"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setError(null);
          }}
          placeholder="Enter YouTube URL (e.g., youtube.com/watch?v=... or youtu.be/...)"
          className={`flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
            error ? 'border-red-300' : ''
          }`}
          onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
        />
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          Add
        </button>
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      <p className="mt-1 text-xs text-gray-500">
        Supported formats: youtube.com/watch?v=..., youtu.be/..., youtube.com/shorts/...
      </p>
    </div>
  );
}